import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';

import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';

import BackHeader from '../../../../components/BackHeader';
import { H2, Label, NotFoundContainer } from '../../../../components/Generic';
import { Container, Content, Card } from './styles';
import AnimalItem from '../../../../components/Animal/Item';
import {
  IconBuildingCommunity,
  IconCirclePlus,
  IconInfoCircle,
  IconLoader,
  IconUser,
} from '@tabler/icons';
import Modal from '../../../../components/Modal';
import PropertyItem from '../../../../components/PropertyItem';
import SearchInput from '../../../../components/Form/SearchInput';
import UserItem from '../../../../components/UserItem';
import { useAuth } from '../../../../hooks/auth';

interface stateType {
  selectedAnimals: any;
}

const NewTransference: React.FC = () => {
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    animals_ids: ['b650263f-a818-48ac-a193-369b31692c00'],
  });

  const [selectedAnimals, setSelectedAnimals] = useState<any[]>([]);

  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [userQuery, setUserQuery] = useState('');

  const { id_property }: any = useParams();
  const { addToast } = useToast();
  const location = useLocation<stateType>();
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    const { selectedAnimals } = location.state;

    setSelectedAnimals(selectedAnimals);
    getData();
  }, []);

  const getData = async () => {
    try {
      const propertiesResponse = await api.get('/propriedades/minhas');
      setProperties(propertiesResponse.data.minhasPropriedades);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUser = useCallback(
    async (key: string) => {
      try {
        setLoading(true);

        const response = await api.get(
          `/pesquisas/usuarios?limite=10&pagina=1&key=${key}`,
        );

        setUsers(response.data.usuarios);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: 'Algo deu errado.',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const createTransference = async (propertyId: string, userId: string) => {
    try {
      const animalsIds = selectedAnimals.map(animal => animal.id);

      console.log(animalsIds);
      console.log(
        `Transfering to ${userId ? 'another user' : 'me'}: ${
          userId || user.id
        }`,
      );

      const transferenceResponse = await api.post(
        '/transferences',
        {
          animals_ids: animalsIds,
        },
        {
          headers: {
            propriedade_id: id_property,
            target_user_id: userId || user.id,
          },
        },
      );

      if (propertyId) {
        await api.put(
          `/transferences/target_property/${transferenceResponse.data.id}`,
          {
            target_property_id: propertyId,
          },
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );

        await api.put(
          `/transferences/confirm/${transferenceResponse.data.id}`,
          {
            receiver_confirmation: true,
          },
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );
      }

      addToast({
        type: 'success',
        title: 'Feito!',
        description: 'Animais transferidos.',
      });

      history.replace(`/propriedade/${id_property}`);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <BackHeader title="Nova transferência" />

      <Content>
        <H2>Para onde enviar os animais?</H2>

        <Card onClick={() => setIsPropertiesModalOpen(true)}>
          <IconBuildingCommunity size={32} />
          <div>
            <strong>Para outra propriedade sua</strong>
          </div>
        </Card>

        <Card onClick={() => setIsUsersModalOpen(true)}>
          <IconUser size={32} />
          <div>
            <strong>Para a propriedade de outro usuário</strong>
          </div>
        </Card>

        <hr />

        <Label>{selectedAnimals.length} animais selecionados</Label>

        <div className="animal-list">
          {selectedAnimals?.map((animal: any) => (
            <AnimalItem
              key={animal.id}
              imageUrl={
                animal.imagens?.length > 0 ? animal.imagens[0]?.image_url : ''
              }
              name={animal.nome}
              birth={animal.data_de_nascimento}
              number={animal.numero_de_identificacao}
              gender={animal.sexo}
              state={animal.estado}
              isPrivate={animal.privado}
              belongs={animal.pertencente}
            />
          ))}
        </div>
      </Content>

      <Modal
        isOpen={isPropertiesModalOpen}
        onClose={() => setIsPropertiesModalOpen(false)}
      >
        <H2>Escolha uma propriedade</H2>

        {properties?.length > 0 ? (
          properties?.map(property => (
            <PropertyItem
              key={property.id}
              location={property.descricao}
              name={property.nome}
              imageUrl={
                property.imagens?.length > 0
                  ? property.imagens[0]?.image_url
                  : ''
              }
              onClick={() => createTransference(property.id, '')}
            />
          ))
        ) : (
          <NotFoundContainer>
            <IconInfoCircle size={24} />

            <strong>Nenhuma propriedade encontrada.</strong>
          </NotFoundContainer>
        )}
      </Modal>

      <Modal
        isOpen={isUsersModalOpen}
        onClose={() => setIsUsersModalOpen(false)}
      >
        <H2>Pesquisar usuário</H2>

        <SearchInput
          placeholder="Digite o email ou o nome"
          onChange={(e: any) => setUserQuery(e.target.value)}
          onSubmit={() => handleSearchUser(userQuery)}
          style={{ marginBottom: 20 }}
          isLoading={loading}
        />

        {users?.map((user: any) => (
          <UserItem
            key={user.id}
            avatar_url={user.url}
            name={user.nome}
            description={user.email}
            style={{ marginTop: 20, cursor: 'pointer' }}
            onClick={() => createTransference('', user.id)}
          />
        ))}
      </Modal>
    </Container>
  );
};

export default NewTransference;
